import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import RefreshIcon from "@mui/icons-material/Refresh";
import AxiosInstance from "../../services/networkServices";
import PageLayout from "../../components/PageLayout";
import CommonPagination from "../../components/Table/CommonPagination";
import { showToast } from "../../store/slices/common";

const PAGE_SIZE = 20;

const STATUS_TABS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "all", label: "All" },
];

const TYPE_FILTERS = [
  { value: "", label: "Posts & comments" },
  { value: "POST", label: "Posts" },
  { value: "COMMENT", label: "Comments" },
];

const APPEAL_CHIP = {
  PENDING: { label: "Pending", color: "warning" },
  APPROVED: { label: "Approved", color: "success" },
  REJECTED: { label: "Rejected", color: "default" },
};

const ENFORCEMENT_LABEL = {
  DISABLE: "Content removed",
  DISABLE_AND_BAN: "Removed + 30-day ban",
};

const cardSx = {
  padding: "20px",
  borderRadius: "10px",
  boxShadow:
    "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  boxSizing: "border-box",
};

const headCellSx = {
  fontWeight: 700,
  color: "#142C5C",
  backgroundColor: "#f4f6fb",
  fontSize: "13px",
  whiteSpace: "nowrap",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const formatDay = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const formatConfidence = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? `${Math.round(value)}%`
    : null;

/** Removed content preview: comment text or post thumbnail + caption. */
const ContentPreview = ({ item }) => {
  const snapshot = item.contentSnapshot || {};
  if (item.targetType === "COMMENT") {
    return (
      <Typography
        sx={{
          fontSize: "13px",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxWidth: 320,
        }}
      >
        {snapshot.text || "—"}
      </Typography>
    );
  }
  const image = snapshot.thumbnail || null;
  const mediaUrl = snapshot.originalMedia || snapshot.media || null;
  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", maxWidth: 320 }}>
      {image ? (
        <a href={mediaUrl || image} target="_blank" rel="noreferrer">
          <Avatar
            variant="rounded"
            src={image}
            alt="post"
            sx={{ width: 64, height: 64 }}
          />
        </a>
      ) : (
        <Avatar variant="rounded" sx={{ width: 64, height: 64 }}>
          {snapshot.postType?.[0] || "P"}
        </Avatar>
      )}
      <Box>
        {snapshot.postType && (
          <Typography variant="caption" color="text.secondary" display="block">
            {snapshot.postType}
          </Typography>
        )}
        <Typography sx={{ fontSize: "13px", wordBreak: "break-word" }}>
          {snapshot.caption || "—"}
        </Typography>
        {mediaUrl && (
          <a
            href={mediaUrl}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: "12px" }}
          >
            Open media
          </a>
        )}
      </Box>
    </Box>
  );
};

/** What the AI concluded — matched categories + top raw labels. */
const AnalysisCell = ({ item }) => {
  const analysis = item.analysis || {};
  const matched = analysis.matchedCategories || [];
  const labels = (analysis.labels || []).slice(0, 6);
  const tooltip = [
    analysis.summary || null,
    analysis.modelId ? `Model: ${analysis.modelId}` : null,
    item.provider ? `Provider: ${item.provider}` : null,
    analysis.analyzedAt ? `Analyzed: ${formatDate(analysis.analyzedAt)}` : null,
    labels.length
      ? `Labels: ${labels
          .map((l) => {
            const conf = formatConfidence(l.confidence);
            return conf ? `${l.name} (${conf})` : l.name;
          })
          .join(", ")}`
      : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 220 }}>
        {matched.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            {analysis.summary || "—"}
          </Typography>
        )}
        {matched.map((category) => {
          const best = (analysis.labels || []).find(
            (l) => l.name === category || l.parentName === category
          );
          const conf = formatConfidence(best?.confidence);
          return (
            <Chip
              key={category}
              size="small"
              color="error"
              variant="outlined"
              label={conf ? `${category} · ${conf}` : category}
              sx={{ height: 20, fontSize: 11 }}
            />
          );
        })}
      </Box>
    </Tooltip>
  );
};

/**
 * Appeals admin page: users contesting content that automated moderation
 * removed. Approve restores the content (and lifts the 30-day comment ban
 * tied to it); Reject leaves the removal in place. Either way the user is
 * notified.
 */
const ManageAppeals = () => {
  const dispatch = useDispatch();

  const [statusTab, setStatusTab] = useState("PENDING");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0 });
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [statusTab, typeFilter, debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    const fetchList = async () => {
      setListLoading(true);
      try {
        const response = await AxiosInstance.post("/admin/moderation/appeals/list", {
          page: page + 1,
          limit: PAGE_SIZE,
          status: statusTab,
          ...(typeFilter && { targetType: typeFilter }),
          ...(debouncedSearch && { search: debouncedSearch }),
        });
        if (cancelled) return;
        const data = response.data?.data || {};
        setRows(data.list || []);
        setTotal(data.resultCount ?? (data.list || []).length);
        if (data.counts) setCounts(data.counts);
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching appeals:", error);
        dispatch(
          showToast({
            color: "error",
            msg: error?.response?.data?.message || "Failed to load appeals.",
          })
        );
      } finally {
        if (!cancelled) {
          setListLoading(false);
          setLoading(false);
        }
      }
    };
    fetchList();
    return () => {
      cancelled = true;
    };
  }, [page, statusTab, typeFilter, debouncedSearch, refreshToken, dispatch]);

  // ---- Actions ----------------------------------------------------------------
  const [pendingAction, setPendingAction] = useState(null); // { item, action }
  const [actionNote, setActionNote] = useState("");
  const [acting, setActing] = useState(false);

  const openAction = (item, action) => {
    setPendingAction({ item, action });
    setActionNote("");
  };

  const runAction = async () => {
    if (!pendingAction) return;
    const { item, action } = pendingAction;
    try {
      setActing(true);
      const response = await AxiosInstance.post("/admin/moderation/appeals/action", {
        caseId: item._id,
        action,
        ...(actionNote.trim() && { note: actionNote.trim() }),
      });
      if (response.data.status) {
        const username = item.owner?.username ? `@${item.owner.username}` : "The user";
        dispatch(
          showToast({
            color: "success",
            msg:
              action === "APPROVE"
                ? `Appeal approved — ${username}'s ${item.targetType.toLowerCase()} restored.`
                : `Appeal rejected — removal stands for ${username}.`,
          })
        );
        setPendingAction(null);
        setRefreshToken((t) => t + 1);
      } else {
        dispatch(
          showToast({
            color: "error",
            msg: response.data.message || "Action failed.",
          })
        );
      }
    } catch (error) {
      console.error("Appeal action failed:", error);
      dispatch(
        showToast({
          color: "error",
          msg: error?.response?.data?.message || "Action failed.",
        })
      );
    } finally {
      setActing(false);
    }
  };

  const tabLabel = (tab) => {
    if (tab.value === "all") return tab.label;
    const n = counts[tab.value];
    return n ? `${tab.label} (${n})` : tab.label;
  };

  return (
    <PageLayout
      title="Appeals"
      subtitle="Users contesting content that automated moderation removed. Approving restores the content and lifts any ban tied to it."
      loading={loading}
      actions={
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => setRefreshToken((t) => t + 1)}
          disabled={listLoading}
        >
          Refresh
        </Button>
      }
    >
      <Paper sx={cardSx}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            mb: 2,
          }}
        >
          <Tabs
            value={statusTab}
            onChange={(_, v) => setStatusTab(v)}
            sx={{ flexGrow: 1, minHeight: 40 }}
          >
            {STATUS_TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tabLabel(tab)}
                sx={{ minHeight: 40, textTransform: "none", fontWeight: 600 }}
              />
            ))}
          </Tabs>
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            {TYPE_FILTERS.map((o) => (
              <MenuItem key={o.value || "all"} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Search user / content / reason"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>User</TableCell>
                <TableCell sx={headCellSx}>Type</TableCell>
                <TableCell sx={headCellSx}>Content</TableCell>
                <TableCell sx={headCellSx}>Reported for</TableCell>
                <TableCell sx={headCellSx}>AI findings</TableCell>
                <TableCell sx={headCellSx}>Enforcement</TableCell>
                <TableCell sx={headCellSx}>Appeal</TableCell>
                <TableCell sx={headCellSx}>Submitted</TableCell>
                <TableCell sx={headCellSx} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {listLoading ? "Loading…" : "No appeals here."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((item) => {
                const appeal = item.appeal || {};
                const enforcement = item.enforcement || {};
                const chip = APPEAL_CHIP[appeal.status] || APPEAL_CHIP.PENDING;
                const owner = item.owner || {};
                const isPending = appeal.status === "PENDING";
                return (
                  <TableRow key={item._id} hover>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Avatar
                          src={owner.profilePicture || undefined}
                          sx={{ width: 32, height: 32 }}
                        >
                          {owner.username?.[0]?.toUpperCase() || "?"}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                            @{owner.username || "unknown"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {owner.email || ""}
                            {owner.isDeleted ? " · deleted" : ""}
                            {owner.isAccountBlocked ? " · blocked" : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={item.targetType === "POST" ? "Post" : "Comment"}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <ContentPreview item={item} />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 200 }}>
                        {(item.reportIssueLabels || item.reportIssues || []).map((label) => (
                          <Chip
                            key={label}
                            size="small"
                            label={label}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <AnalysisCell item={item} />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: 13 }}>
                        {ENFORCEMENT_LABEL[enforcement.action] || enforcement.action || "—"}
                      </Typography>
                      {enforcement.banUntil && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Ban until {formatDay(enforcement.banUntil)}
                        </Typography>
                      )}
                      {enforcement.revertedAt && (
                        <Typography variant="caption" color="success.main" display="block">
                          Restored {formatDay(enforcement.revertedAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Chip size="small" label={chip.label} color={chip.color} sx={{ mb: 0.5 }} />
                      <Typography
                        sx={{
                          fontSize: 12,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxWidth: 260,
                          fontStyle: "italic",
                        }}
                      >
                        “{appeal.reason || "—"}”
                      </Typography>
                      {appeal.status !== "PENDING" && (
                        <Tooltip title={appeal.note || ""}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.reviewer?.username ? `by @${item.reviewer.username}` : "reviewed"}{" "}
                            · {formatDate(appeal.reviewedAt)}
                            {appeal.note ? " · note" : ""}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                      {formatDate(appeal.submittedAt)}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                      {isPending ? (
                        <>
                          <Button
                            size="small"
                            color="success"
                            onClick={() => openAction(item, "APPROVE")}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => openAction(item, "REJECT")}
                          >
                            Reject
                          </Button>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {total > PAGE_SIZE && (
          <Box sx={{ mt: 2 }}>
            <CommonPagination
              count={total}
              page={page}
              rowsPerPage={PAGE_SIZE}
              onPageChange={(_, newPage) => setPage(newPage)}
            />
          </Box>
        )}
      </Paper>

      <Dialog
        open={!!pendingAction}
        onClose={() => (acting ? null : setPendingAction(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {pendingAction?.action === "APPROVE" ? "Approve appeal" : "Reject appeal"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {pendingAction?.action === "APPROVE"
              ? `Restore @${pendingAction.item.owner?.username || "the user"}'s ${pendingAction.item.targetType.toLowerCase()}${
                  pendingAction.item.enforcement?.banUntil ? " and lift their ban" : ""
                }. They will be notified that the appeal was approved.`
              : `Keep @${pendingAction?.item.owner?.username || "the user"}'s ${pendingAction?.item.targetType.toLowerCase()} removed${
                  pendingAction?.item.enforcement?.banUntil ? " and the ban in place" : ""
                }. They will be notified that the appeal was rejected.`}
          </Typography>
          <TextField
            label="Internal note (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)} disabled={acting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pendingAction?.action === "APPROVE" ? "success" : "error"}
            onClick={runAction}
            disabled={acting}
          >
            {acting ? "Working…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default ManageAppeals;
